import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { track } from "@/lib/analytics";

export default function QuizLesson({ lesson, onCompleted }) {
  const questions = lesson.payload.questions || [];
  const passing = lesson.payload.passing_score ?? 70;
  const [answers, setAnswers] = useState(Array(questions.length).fill(null));
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (answers.some((a) => a === null)) { toast.error("Responde todas las preguntas"); return; }
    setSubmitting(true);
    try {
      const r = await api.post(`/alumno/quiz/${lesson.id}/submit`, { answers });
      setResult(r.data);
      track("quiz_submitted", { lesson_id: lesson.id, score: r.data.score, passed: r.data.passed });
      if (r.data.passed) {
        toast.success(`¡Aprobado! ${r.data.score}%`);
        setTimeout(() => onCompleted(), 1200);
      } else {
        toast.error(`Puntaje ${r.data.score}%. Necesitas ${passing}% para aprobar.`);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="lesson-quiz">
      <p className="text-sm text-gray-400 mb-4">Puntaje mínimo para aprobar: <span className="text-white font-semibold">{passing}%</span></p>
      <div className="space-y-6">
        {questions.map((q, qi) => {
          const fb = result?.feedback?.[qi];
          return (
            <div key={q.id} className="p-4 border border-gray-800 rounded-lg bg-[#0B0F14]" data-testid={`quiz-question-${qi}`}>
              <p className="text-white font-medium mb-3">{qi + 1}. {q.question}</p>
              <div className="space-y-2">
                {q.options.map((opt, oi) => {
                  const selected = answers[qi] === oi;
                  const isCorrect = fb && fb.correct_index === oi;
                  const wrongPick = fb && !fb.correct && selected;
                  return (
                    <button
                      key={oi}
                      disabled={!!result}
                      onClick={() => setAnswers(answers.map((a, i) => i === qi ? oi : a))}
                      data-testid={`quiz-option-${qi}-${oi}`}
                      aria-pressed={selected}
                      className={`w-full text-left px-3 py-2 rounded border text-sm transition-colors ${
                        result
                          ? isCorrect ? "border-emerald-500 bg-emerald-500/10 text-emerald-200"
                            : wrongPick ? "border-red-500 bg-red-500/10 text-red-200"
                            : "border-gray-800 text-gray-500"
                          : selected ? "border-[#3B82F6] bg-[#3B82F6]/10 text-white"
                            : "border-gray-800 text-gray-300 hover:bg-white/5"
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
              {fb && fb.explanation && (
                <p className="mt-3 text-xs text-gray-400 italic border-t border-gray-800 pt-2">{fb.explanation}</p>
              )}
            </div>
          );
        })}
      </div>
      {!result ? (
        <button
          onClick={submit}
          disabled={submitting}
          data-testid="quiz-submit-btn"
          className="mt-6 px-6 py-2.5 rounded-md bg-[#B84A0C] hover:bg-primary text-white font-semibold disabled:opacity-60"
        >
          {submitting ? "Enviando..." : "Enviar respuestas"}
        </button>
      ) : (
        <div className={`mt-6 p-4 rounded-lg border ${result.passed ? "border-emerald-500/40 bg-emerald-500/10" : "border-red-500/40 bg-red-500/10"}`} data-testid="quiz-result">
          <p className={`font-bold ${result.passed ? "text-emerald-300" : "text-red-300"}`}>
            {result.passed ? "Aprobado" : "No aprobado"} · Puntaje: {result.score}%
          </p>
          <p className="text-sm text-gray-300 mt-1">{result.correct}/{result.total} correctas.</p>
          {!result.passed && (
            <button
              onClick={() => { setResult(null); setAnswers(Array(questions.length).fill(null)); }}
              data-testid="quiz-retry-btn"
              className="mt-3 text-xs px-3 py-1.5 rounded border border-gray-700 text-white hover:bg-white/5"
            >
              Reintentar
            </button>
          )}
        </div>
      )}
    </div>
  );
}
